import { alpha, getOverlayAlpha } from '@mui/material/styles';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { Link } from 'react-router';

import { courseCardClicked } from 'my-core/gtm-events';
import { coursePath, studentCoursePagePath } from 'my-core/routes';
import { pluralize } from 'my-utils';

import SubjectIcon from 'my-elements/SubjectIcon';

import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { faUsersClass, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = theme => {
  const {
    palette,
    shadows,
    shape: { borderRadius },
    spacing,
  } = theme;
  return {
    borderRadius,
    padding: spacing(2),
    backgroundColor: palette.background.paper,
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'left',

    boxShadow: shadows.states.clickable,
    ...theme.applyStyles('dark', {
      backgroundImage: `linear-gradient(${alpha('#fff', getOverlayAlpha(4))}, ${alpha('#fff', getOverlayAlpha(4))})`,
      boxShadow: 'none',
    }),
    '&:hover': {
      boxShadow: shadows.states.hover,
      ...theme.applyStyles('dark', {
        backgroundImage: `linear-gradient(${alpha('#fff', getOverlayAlpha(8))}, ${alpha('#fff', getOverlayAlpha(8))})`,
        boxShadow: 'none',
      }),
    },

    '.main': { flex: 1 },
    '.mainHeader': { display: 'flex', alignItems: 'center' },
    '.courseIcon': {
      width: 25,
      height: 25,
      marginRight: spacing(2),
    },
    '.courseIcon_large': { width: 40, height: 40 },
    '.divider': {
      margin: spacing(1, 0),
      width: '100%',
      flex: '0 0 1px',
    },
    '.actions': {
      display: 'flex',
      flex: `0 0 ${spacing(3)}`,
    },
    '.stats': {
      display: 'flex',
      alignItems: 'center',
    },
    '.stat': {
      marginRight: spacing(2),
      '> svg': {
        marginRight: spacing(0.5),
        color: palette.text.secondary,
      },
    },
    '.addBtn': { marginLeft: 'auto' },
    '.skeleton': { display: 'inline-block' },
  };
};

const DEFAULT_COURSE = {};
const CourseCard = forwardRef(function CourseCard(props, ref) {
  const {
    addCourseStatus,
    className,
    course = DEFAULT_COURSE,
    hideStats,
    loading,
    onAddCourse,
    origSchoolId,
    showSchool,
    toInCourseUX,
  } = props;

  return (
    <ButtonBase
      className={className}
      component={loading ? undefined : Link}
      css={rootCss}
      disabled={!!loading}
      nativeButton={loading ? undefined : false}
      onClick={() => courseCardClicked(course)}
      ref={ref}
      to={
        loading ? undefined
        : toInCourseUX ?
          studentCoursePagePath(course)
        : coursePath(course, origSchoolId)
      }
    >
      <>
        <div className="main">
          <div className="mainHeader">
            {loading ?
              <Skeleton className={classNames('courseIcon', { courseIcon_large: showSchool })} variant="circular" />
            : <SubjectIcon className={classNames('courseIcon', { courseIcon_large: showSchool })} course={course} />}
            <div>
              <Typography sx={{ color: 'text.secondary' }} variant="overline">
                {showSchool &&
                  (loading ?
                    <Skeleton variant="text" width={100} />
                  : course.school_short_name || course.school?.short_name || course.school_name || course.school?.name)}
              </Typography>
              <Typography variant="h5">
                {loading ?
                  <Skeleton variant="text" width={150} />
                : course.code}
              </Typography>
            </div>
          </div>
          <Typography sx={{ color: 'text.secondary' }}>
            {loading ?
              <Skeleton variant="text" width="90%" />
            : course.name}
          </Typography>
        </div>

        {(!hideStats || onAddCourse) && (
          <>
            {!hideStats && <Divider className="divider" />}
            <div className="actions">
              {!hideStats && (
                <div className="stats">
                  {/*renderStat(
                    course.stats?.total_students > 25,
                    `${pluralize(course.stats?.total_students, 'student')} have taken this course on Wizeprep`,
                    faUserGraduate,
                    course.stats?.total_students
                  )*/}
                  {renderStat(
                    course.stats?.total_video_mins,
                    `${pluralize((course.stats?.total_video_mins / 60).toFixed(1), 'hour')} of video lessons`,
                    faVideo,
                    'On-demand prep',
                  )}
                  {renderStat(
                    course.stats?.public_study_sessions,
                    `${pluralize(course.stats?.public_study_sessions, 'live-prep session')}`,
                    faUsersClass,
                    'Live prep',
                  )}
                </div>
              )}

              {onAddCourse && (
                <Button
                  className="addBtn"
                  disabled={addCourseStatus?.fetching}
                  onClick={e => {
                    e.preventDefault();
                    onAddCourse(course.id);
                  }}
                  onMouseDown={e => e.stopPropagation()}
                  size="small"
                >
                  Add Course
                </Button>
              )}
            </div>
          </>
        )}
      </>
    </ButtonBase>
  );

  function renderStat(shouldShow, tooltip, icon, stat) {
    if (loading || shouldShow) {
      return (
        <Tooltip title={tooltip}>
          <Typography className="stat" color="primary">
            <FontAwesomeIcon icon={icon} />
            {loading ?
              <Skeleton className="skeleton" variant="text" width={60} />
            : stat}
          </Typography>
        </Tooltip>
      );
    }
  }
});

export default CourseCard;
